import { Form, InputNumber, Segmented, Tag } from "antd";
import { ProgramSplitFormat } from "dtos/strategy.dto";
import { formatMoney } from "utils/functions";
import { AnswerButtons, ChoiceCards, CountStepper, Question, StepHeading } from "./ui";

type Step = { debt_cap: number; weeks: number };

/** A tiny bar showing how much of the plan carries commission. */
const SplitGlyph = ({ share }: { share: number }) => (
  <span className="flex w-full h-2.5 rounded bg-blue-100 overflow-hidden">
    <span className="h-full bg-emerald-500 rounded" style={{ width: `${share}%` }} />
  </span>
);

const SPLIT_OPTIONS = [
  {
    value: ProgramSplitFormat.UNIFIED,
    title: "Across the whole plan",
    description: "Spread evenly over every payment.",
    share: 100,
  },
  {
    value: ProgramSplitFormat.EARLY_STOPPING_WEEKS,
    title: "For a set number of weeks",
    description: "Collect first, then everything goes to creditors.",
    share: 40,
  },
  {
    value: ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE,
    title: "Until a share of the plan",
    description: "Stop once a % of the plan is complete.",
    share: 60,
  },
  {
    value: ProgramSplitFormat.PERCENTAGE_OF_WEEKLY,
    title: "A share of each payment",
    description: "A fixed % of every payment until it’s paid.",
    share: 70,
  },
];

const CommissionStep = ({ totalDebt }: { totalDebt: number }) => {
  const form = Form.useFormInstance();
  const format: ProgramSplitFormat | undefined = Form.useWatch(["program_split", "format"], form);
  const mode: "fixed" | "variable" = Form.useWatch(["program_split", "meta", "mode"], form) ?? "variable";
  const base: "debt" | "interest" = Form.useWatch(["program_split", "meta", "base"], form) ?? "debt";
  const steps: Step[] = Form.useWatch(["program_split", "meta", "steps"], form) ?? [];
  const initialTimeline: number = Form.useWatch(["program_split", "initial_payment_timeline"], form) ?? 0;

  // Moving between debt and interest keeps the ladder roughly equivalent (interest ≈ 25% of debt).
  const changeBase = (next: "debt" | "interest") => {
    if (next === base) return;
    const multiplier = next === "interest" ? 0.25 : 4;
    form.setFieldValue(["program_split", "meta", "base"], next);
    form.setFieldValue(
      ["program_split", "meta", "steps"],
      steps.map((step) => ({
        debt_cap: Math.ceil((step.debt_cap ?? 0) * multiplier),
        weeks: Math.ceil((step.weeks ?? 0) * multiplier),
      })),
    );
  };

  const matchIndex = (() => {
    if (base !== "debt" || !steps.length) return -1;
    const idx = steps.findIndex((step, i) => i < steps.length - 1 && totalDebt <= step.debt_cap);
    return idx === -1 ? steps.length - 1 : idx;
  })();

  return (
    <>
      <StepHeading
        step={3}
        title="How should the commission be collected?"
        description="Pick one — we’ll only ask what that option needs."
      />

      <Form.Item name={["program_split", "format"]} noStyle>
        <ChoiceCards<ProgramSplitFormat>
          columns={2}
          options={SPLIT_OPTIONS.map(({ share, ...option }) => ({ ...option, visual: <SplitGlyph share={share} /> }))}
        />
      </Form.Item>

      <div className="mt-4 mb-6 p-4 bg-gray-50 border border-solid border-gray-200 rounded-xl">
        {format === ProgramSplitFormat.UNIFIED && (
          <div className="text-[13px] text-gray-700">
            Commission is spread evenly over every payment until the plan ends. Nothing else to set.
          </div>
        )}

        {(format === ProgramSplitFormat.EARLY_STOPPING_PERCENTAGE ||
          format === ProgramSplitFormat.PERCENTAGE_OF_WEEKLY) && (
          <Question
            className="!mb-0"
            title={
              format === ProgramSplitFormat.PERCENTAGE_OF_WEEKLY
                ? "What share of each payment goes to commission?"
                : "Stop collecting after what share of the plan?"
            }
          >
            <Form.Item name={["program_split", "meta", "percentage"]} noStyle>
              <InputNumber min={0} max={100} suffix="%" className="w-60" />
            </Form.Item>
          </Question>
        )}

        {format === ProgramSplitFormat.EARLY_STOPPING_WEEKS && (
          <>
            <Question
              title="Same number of weeks for every debt size?"
              className={mode === "fixed" ? "!mb-3" : "!mb-4"}
            >
              <Form.Item name={["program_split", "meta", "mode"]} noStyle>
                <AnswerButtons<"fixed" | "variable">
                  options={[
                    { value: "fixed", label: "Yes" },
                    { value: "variable", label: "No, it depends on the debt" },
                  ]}
                />
              </Form.Item>
            </Question>

            {mode === "fixed" ? (
              <Form.Item
                name={["program_split", "meta", "fixed_weeks"]}
                label="Collect commission for"
                rules={[{ required: true, message: "Enter the number of weeks" }]}
                className="!mb-0 w-56"
              >
                <InputNumber suffix="weeks" min={1} className="w-full" />
              </Form.Item>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-xs text-gray-600">Measure the size by</span>
                  <Segmented
                    size="small"
                    value={base}
                    onChange={changeBase}
                    options={[
                      { label: "Total debt", value: "debt" },
                      { label: "Total interest", value: "interest" },
                    ]}
                  />
                </div>
                <Form.List name={["program_split", "meta", "steps"]}>
                  {(fields) => (
                    <div className="flex flex-col bg-white border border-solid border-gray-200 rounded-lg overflow-hidden">
                      {fields.map(({ key, name }, i) => {
                        const isLast = i === fields.length - 1;
                        const threshold = isLast ? (steps[i - 1]?.debt_cap ?? 0) : (steps[i]?.debt_cap ?? 0);
                        const label = `${base === "debt" ? "Debt" : "Interest"} ${isLast ? ">" : "≤"} ${formatMoney(threshold)}`;
                        return (
                          <div
                            key={key}
                            className={`flex items-center gap-2.5 px-3 py-2 ${i ? "border-0 border-t border-solid border-gray-100" : ""} ${
                              i === matchIndex ? "bg-violet-50" : ""
                            }`}
                          >
                            <span className="text-[13px] text-gray-700 grow">{label}</span>
                            {i === matchIndex && (
                              <Tag color="purple" className="!m-0">
                                This customer
                              </Tag>
                            )}
                            <Form.Item
                              name={[name, "weeks"]}
                              rules={[{ required: true, message: "Required" }]}
                              className="!mb-0"
                            >
                              <InputNumber aria-label={label} suffix="weeks" min={1} className="w-32" />
                            </Form.Item>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Form.List>
              </>
            )}
          </>
        )}
      </div>

      <Question
        title="Spread the initial payment over extra weeks?"
        hint="0 means it’s collected with the first payment. 2 means it’s split over 3 payments."
      >
        <div className="flex items-center gap-3">
          <Form.Item name={["program_split", "initial_payment_timeline"]} noStyle>
            <CountStepper />
          </Form.Item>
          <span className="text-[13px] text-gray-600">
            {initialTimeline ? `Split over ${initialTimeline + 1} payments` : "Collected with the first payment"}
          </span>
        </div>
      </Question>
    </>
  );
};

export default CommissionStep;
